import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { modelQuery, modelVersionsQuery } from '@/features/ml/models/model-api';
import { ModelVersionDetailPage } from '@/features/ml/models/model-version-detail-page';
import { isVersionTab, type VersionTab } from '@/features/ml/models/model-tabs';
import { ConsoleShell } from '@/features/shell/console-shell';

/** Détail d'une version de modèle : étape, run d'origine, artefact. Onglet dans l'URL (`?tab=artifact`). */
export const Route = createFileRoute('/$tenant/models/$modelId/versions/$versionId')({
  validateSearch: (search: Record<string, unknown>): { tab: VersionTab } => ({
    tab: isVersionTab(search['tab']) ? search['tab'] : 'summary',
  }),
  component: VersionRoute,
});

function VersionRoute() {
  const { tenant, modelId, versionId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();
  const model = useQuery(modelQuery(tenant, modelId));
  const versions = useQuery(modelVersionsQuery(tenant, modelId));
  const name = versions.data?.items.find((version) => version.id === versionId)?.name;
  return (
    <ConsoleShell
      tenantId={tenant}
      activeItem="models"
      crumbs={[
        { label: t('nav.items.models') },
        { label: model.data?.name ?? modelId },
        { label: name ? t('ml.versions.label', { name }) : versionId, mono: true },
      ]}
    >
      <ModelVersionDetailPage
        tenantId={tenant}
        modelId={modelId}
        versionId={versionId}
        tab={tab}
        onTabChange={(next) => void navigate({ search: { tab: next }, replace: true })}
      />
    </ConsoleShell>
  );
}
